import React, { useState, useEffect } from 'react';
import {
  Film, FileText, Mic, Music, Image as ImageIcon, Layers, User, Calendar,
  CheckCircle2, AlertTriangle, Lock, Unlock, Sparkles, Loader2, X,
  Play, ExternalLink, ChevronRight, Scissors, UserPlus, UserMinus, Eye, Upload,
} from 'lucide-react';

/**
 * Compact "Upload <category>" pill button used on the Animatics & Script
 * work-cards. Renders as a label so the hidden file input is keyboard +
 * click accessible. Visual states: idle (colored) / uploading (zinc spinner).
 */
function UploadButton({ category, accept, uploading, onChange, taskId, colorClass, icon: Icon, label }) {
  return (
    <label data-testid={`work-card-upload-${category}-${taskId}`}
      className={`px-2.5 py-1.5 rounded text-[10px] font-medium flex items-center gap-1.5 cursor-pointer transition-all border ${
        uploading ? 'bg-zinc-800 text-zinc-500 border-zinc-800 cursor-wait' : colorClass
      }`}>
      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {uploading ? 'Uploading…' : label}
      <input type="file" hidden onChange={onChange} accept={accept} disabled={uploading} />
    </label>
  );
}

/**
 * WorkCard — the unit of work in the new Allotment view.
 * Renders ONE task as a rich card with:
 *   • Title + status pill
 *   • Raw-material strip (script, VO, concept art, animatic, CBA clips, etc.)
 *     — pulled live from /api/tasks/{id}/inputs
 *   • Assignee + deadline
 *   • Action buttons: [Assign / Reassign] · [CBA Tools] (animatics only)
 *
 * Used for sequence-mode and asset-mode tasks. Shot-mode tasks render
 * grouped under their sequence (see ShotGroupCard).
 */
const WORK_UNIT_ICON = {
  sequence: Film, asset: User, shot: Layers,
};

const STAGE_ICON = {
  script:      FileText,
  storyboard:  ImageIcon,
  animatics:   Film,
  animatic:    Film,
  // PIPELINE REFORM R4 — CBA = Cut-By-Animatics, owns the scissors icon
  cba:         Scissors,
  sound:       Music,
  music:       Music,
  concept_art: Sparkles,
  // PIPELINE REFORM R3 — modeling+texturing merged into asset_build
  asset_build: Layers,
  modeling:    Layers,
  texturing:   Layers,
  rigging:     User,
  layout:      Eye,
  animation:   Play,
  lighting:    Sparkles,
  fx:          Sparkles,
  comp:        Layers,
  render:      Film,
};

const STATUS_PILL = {
  unassigned:        { label: 'READY',      cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  assigned:          { label: 'ASSIGNED',   cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  in_progress:       { label: 'IN PROGRESS',cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  in_review:         { label: 'IN REVIEW',  cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  client_review:     { label: 'CLIENT GATE',cls: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30' },
  client_revise:     { label: 'CHANGES REQ',cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  blocked:           { label: 'BLOCKED',    cls: 'bg-zinc-800 text-zinc-500 border-zinc-700' },
  completed:         { label: 'DONE',       cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  approved:          { label: 'APPROVED',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  final_approved:    { label: 'APPROVED',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
};

export default function WorkCard({
  task, api, projectId, internal = [], freelancers = [], vendors = [],
  onAssignClick, onUnassignClick, onCBAClick, onBriefClick, onRefresh, onTaskOpen,
  showCBATools = false,   // CBA Tools is wrong context in Allotment — opt-in only
}) {
  const [inputs, setInputs] = useState(null);
  const [loadingInputs, setLoadingInputs] = useState(false);
  const [expanded, setExpanded] = useState(true); // sequence cards always start expanded
  // PIPELINE REFORM R5 — Generic brief upload (script | voiceover | concept_art).
  // Renders as a small label-button trio on the Animatics card so PMs can
  // attach raw materials manually for contingency.
  const [uploading, setUploading] = useState(null);   // 'script' | 'voiceover' | 'concept_art' | null
  const [uploadMsg, setUploadMsg] = useState('');

  const uploadBrief = async (e, category, opts = {}) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    setUploading(category); setUploadMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Script keeps its dedicated endpoint for back-compat; everything else
      // uses the generic /brief/upload route with `category`.
      let r;
      if (category === 'script') {
        r = await api.post(`/projects/${projectId}/script/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        fd.append('category', category);
        // PIPELINE REFORM R7 — pin a reference to this stage so it doesn't
        // pollute other shot cards. PMs can still upload project-wide refs
        // by omitting target_stage in a different UI flow.
        if (opts.target_stage) fd.append('target_stage', opts.target_stage);
        r = await api.post(`/projects/${projectId}/brief/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      const cleanCat = category.replace('_', ' ');
      const stageNote = opts.target_stage ? ` for ${opts.target_stage}` : '';
      setUploadMsg(`✓ ${r.data.filename} uploaded as ${cleanCat}${stageNote}`);
      await fetchInputs();
      if (onRefresh) onRefresh();
      setTimeout(() => setUploadMsg(''), 4000);
    } catch (err) {
      setUploadMsg(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };
  const Mode = WORK_UNIT_ICON[task.work_unit_type] || Layers;
  const Stage = STAGE_ICON[task.task_type] || Layers;
  const pill = STATUS_PILL[task.status] || STATUS_PILL.unassigned;
  // PIPELINE REFORM R4 — CBA Tools button moved to its own task (cba), NEVER on animatics.
  const isCBA = task.task_type === 'cba';
  const isScript = task.task_type === 'script';
  const isAnimatic = task.task_type === 'animatics' || task.task_type === 'animatic';
  // PIPELINE REFORM R7 — Reference upload lives on shot-mode cards
  // (Layout/Animation/Lighting/FX/Comp/Render) so leads can attach
  // lighting refs / lookdev refs / camera-move refs / etc. with optional
  // stage targeting (e.g. "this ref is for Lighting only").
  const isShotMode = task.work_unit_type === 'shot';
  // Founder spec Feb 2026: Lighting cards get a "BRIEF" pill so PMs can
  // fill the per-shot Day/Night/INT/EXT/brief raw materials. Visible
  // only on lighting cards (shot-mode).
  const isLighting = task.task_type === 'lighting' || task.task_type === 'light';
  const cbaLocked = !!task.cba_locked;
  const cbaCount = task.cba_clip_count || 0;

  const fetchInputs = async () => {
    if (!api || !task.id) return;
    setLoadingInputs(true);
    try {
      const r = await api.get(`/tasks/${task.id}/inputs`);
      setInputs(r.data);
    } catch (e) {
      // silent — UI just shows "no inputs"
      setInputs({ inputs: [], upstream: {} });
    } finally {
      setLoadingInputs(false);
    }
  };
  useEffect(() => { if (expanded) fetchInputs(); /* eslint-disable-next-line */ }, [task.id, expanded]);

  // (legacy uploadScript fn removed — uploadBrief handles all categories)

  const assigneeName = task.assigned_to ? (
    internal.find(u => u.id === task.assigned_to)?.full_name ||
    freelancers.find(u => u.id === task.assigned_to)?.full_name ||
    vendors.find(v => v.user_id === task.assigned_to)?.company_name ||
    task.assigned_to.slice(0, 8)
  ) : null;

  return (
    <div className={`bg-zinc-900 border rounded-md overflow-hidden transition-all ${
      task.work_unit_type === 'sequence' ? 'border-violet-500/30' :
      task.work_unit_type === 'asset' ? 'border-cyan-500/30' :
      'border-zinc-800'
    }`} data-testid={`work-card-${task.id}`}>

      {/* HEADER */}
      <div className="px-4 py-3 border-b border-zinc-800/60 flex items-center gap-3">
        <div className={`w-9 h-9 rounded flex items-center justify-center flex-shrink-0 ${
          task.work_unit_type === 'sequence' ? 'bg-violet-500/20 text-violet-300' :
          task.work_unit_type === 'asset' ? 'bg-cyan-500/20 text-cyan-300' :
          'bg-zinc-800 text-zinc-400'
        }`}>
          <Stage className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[13px] font-medium text-white truncate" data-testid={`work-card-title-${task.id}`}>
              {task.title}
            </h3>
            <span className={`text-[8px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${pill.cls}`}
              data-testid={`work-card-status-${task.id}`}>{pill.label}</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono flex items-center gap-1">
              <Mode className="w-2.5 h-2.5" /> {task.work_unit_type || 'shot'}-mode
            </span>
            {cbaLocked && cbaCount > 0 && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" /> CBA · {cbaCount} cuts
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
            {task.deadline && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {String(task.deadline).slice(0, 10)}
              </span>
            )}
            {assigneeName && (
              <span className="flex items-center gap-1 text-emerald-400">
                <User className="w-3 h-3" /> {assigneeName}
              </span>
            )}
            {task.parent_code && (
              <span className="font-mono text-zinc-600">{task.parent_code}</span>
            )}
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
          {/* Upload Script — sequence-mode SCRIPT task: PM uploads the source
              brief (.pdf/.docx/.xlsx). File becomes a raw-material chip on
              this card AND on the downstream Animatics card immediately. */}
          {isScript && (
            <UploadButton category="script"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.rtf"
              uploading={uploading === 'script'}
              onChange={(e) => uploadBrief(e, 'script')}
              taskId={task.id} colorClass="bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border-amber-500/30"
              icon={Upload} label="Upload Script" />
          )}
          {/* PIPELINE REFORM R5 — Animatics card gets two upload buttons for
              Voice Over (audio) and Concept Art (image). Both feed the raw
              material chip set live. */}
          {isAnimatic && (
            <>
              <UploadButton category="voiceover"
                accept=".wav,.mp3,.aac,.m4a,.ogg,.flac,.aiff"
                uploading={uploading === 'voiceover'}
                onChange={(e) => uploadBrief(e, 'voiceover')}
                taskId={task.id} colorClass="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border-emerald-500/30"
                icon={Mic} label="Upload VO" />
              <UploadButton category="concept_art"
                accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.tiff"
                uploading={uploading === 'concept_art'}
                onChange={(e) => uploadBrief(e, 'concept_art')}
                taskId={task.id} colorClass="bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 border-fuchsia-500/30"
                icon={ImageIcon} label="Upload Concept" />
            </>
          )}
          {/* PIPELINE REFORM R7 — Reference upload on shot-mode cards (Layout/
              Animation/Lighting/FX/Comp/Render). The ref is pinned to THIS
              stage via target_stage so it doesn't pollute other shot tabs. */}
          {isShotMode && (
            <UploadButton category="reference"
              accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.mp4,.mov,.wav,.mp3"
              uploading={uploading === 'reference'}
              onChange={(e) => uploadBrief(e, 'reference', { target_stage: task.task_type })}
              taskId={task.id} colorClass="bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border-cyan-500/30"
              icon={ImageIcon} label="Upload Ref" />
          )}
          {/* Founder spec Feb 2026: BRIEF button on Lighting cards opens the
              PM modal to fill time_of_day + bg_type + free-text brief. The
              saved values surface as a colored chip in the Raw Material strip
              on the artist's MY TASKS card. */}
          {isLighting && (
            <button
              type="button"
              onClick={() => onBriefClick && onBriefClick(task)}
              data-testid={`work-card-brief-${task.id}`}
              title="Fill the lighting brief (Day/Night/INT/EXT + notes) for this shot — becomes raw material for the lighting artist."
              className="px-3 py-1.5 rounded text-[10px] font-medium flex items-center gap-1.5 transition-all bg-violet-500/15 hover:bg-violet-500/25 text-violet-300 border border-violet-500/30"
            >
              <Sparkles className="w-3 h-3" /> Brief
            </button>
          )}
          {/* CBA Tools — visible ONLY on the CBA card. Clicking opens the CBAToolsModal
              targeted at the LINKED animatic task (not the CBA task itself, since the
              modal endpoints operate on animatic media). */}
          {isCBA && showCBATools && (
            <button onClick={() => onCBAClick && onCBAClick(task)}
              data-testid={`work-card-cba-${task.id}`}
              disabled={task.status === 'blocked'}
              title={task.status === 'blocked'
                ? 'Animatic must be client-approved before CBA can run'
                : cbaLocked ? 'CBA cuts are locked — view/unlock' : 'Open CBA Tools to define cuts'}
              className={`px-3 py-1.5 rounded text-[10px] font-medium flex items-center gap-1.5 transition-all ${
                task.status === 'blocked'
                  ? 'bg-zinc-800 text-zinc-600 border border-zinc-800 cursor-not-allowed'
                  : cbaLocked
                    ? 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30'
                    : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white shadow-lg shadow-violet-500/20'
              }`}>
              <Scissors className="w-3 h-3" />
              {cbaLocked ? `CBA · ${cbaCount} cuts` : 'Open CBA Tools'}
            </button>
          )}
          {task.assigned_to ? (
            <button onClick={() => onUnassignClick && onUnassignClick(task)}
              data-testid={`work-card-unassign-${task.id}`}
              className="px-2.5 py-1.5 rounded bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30 text-[10px] font-medium flex items-center gap-1.5">
              <UserMinus className="w-3 h-3" /> Unassign
            </button>
          ) : task.status !== 'blocked' && (
            <button onClick={() => onAssignClick && onAssignClick(task)}
              data-testid={`work-card-assign-${task.id}`}
              className="px-3 py-1.5 rounded bg-gradient-to-r from-fuchsia-500 to-rose-500 hover:from-fuchsia-400 hover:to-rose-400 text-white text-[10px] font-medium flex items-center gap-1.5 shadow-lg shadow-fuchsia-500/20">
              <UserPlus className="w-3 h-3" /> Assign
            </button>
          )}
          {onTaskOpen && (
            <button onClick={() => onTaskOpen(task)}
              data-testid={`work-card-open-${task.id}`}
              title="Open audit trail"
              className="p-1.5 rounded text-zinc-500 hover:text-white hover:bg-zinc-800">
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Upload feedback strip — shared across script / VO / concept-art uploads */}
      {uploadMsg && (
        <div className={`px-4 py-2 text-[10px] border-b border-zinc-800/40 ${
          uploadMsg.startsWith('✓') ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'
        }`} data-testid={`work-card-upload-msg-${task.id}`}>
          {uploadMsg}
        </div>
      )}

      {/* INPUTS STRIP — raw materials the artist needs to start work */}
      {expanded && (
        <div className="px-4 py-3 bg-zinc-950/40 border-b border-zinc-800/40">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
            <Layers className="w-3 h-3" /> Raw Material · Inputs
            {loadingInputs && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
          </div>
          {!inputs ? (
            <p className="text-[10px] text-zinc-600 italic">Loading inputs…</p>
          ) : inputs.inputs?.length === 0 ? (
            <div className="text-[10px] text-zinc-600 italic flex items-center gap-2">
              <AlertTriangle className="w-3 h-3 text-amber-500/70" />
              {(inputs.upstream_types || []).length === 0
                ? 'No upstream stage required for this task.'
                : <>Waiting on upstream: {(inputs.upstream_types || []).map(t => (
                    <span key={t} className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 ml-1">
                      {t.replace(/_/g, ' ')} {inputs.upstream?.[t]?.status === 'completed' ? '✓' : '⌛'}
                    </span>
                  ))}</>
              }
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid={`work-card-inputs-${task.id}`}>
              {inputs.inputs.map((inp, i) => (
                inp.kind === 'brief'
                  ? <BriefChip key={i} brief={inp} />
                  : <InputChip key={i} inp={inp} api={api} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Single raw-material chip — clickable to download/preview the file.
 * Shows an authenticated thumbnail for images; an icon + filename for others.
 */
function InputChip({ inp, api }) {
  const [thumbUrl, setThumbUrl] = useState('');
  const isImage = (inp.mime || '').startsWith('image');
  const isVideo = (inp.mime || '').startsWith('video');
  const isAudio = (inp.mime || '').startsWith('audio');

  useEffect(() => {
    if (!inp.storage_key || !api || (!isImage && !isVideo)) return;
    let cancelled = false; let blobUrl = '';
    (async () => {
      try {
        const r = await api.get(`/reviews/media?key=${encodeURIComponent(inp.storage_key)}`, { responseType: 'blob' });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        setThumbUrl(blobUrl);
      } catch { /* fallback to icon */ }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [inp.storage_key, isImage, isVideo, api]);

  const Icon = isImage ? ImageIcon : isVideo ? Film : isAudio ? Music : FileText;

  return (
    <a
      href={inp.url || '#'}
      target={inp.url ? '_blank' : undefined}
      rel="noreferrer"
      onClick={e => { if (!inp.url) e.preventDefault(); }}
      title={`${inp.label}${inp.duration_sec ? ` · ${inp.duration_sec}s` : ''}`}
      className="inline-flex items-center gap-2 px-2 py-1 rounded bg-zinc-900 border border-zinc-800 hover:border-fuchsia-500/40 hover:bg-zinc-850 transition-all group"
    >
      {thumbUrl ? (
        <span className="w-7 h-7 rounded overflow-hidden bg-zinc-800 flex-shrink-0">
          {isVideo ? (
            <video src={thumbUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={thumbUrl} alt={inp.filename || ''} className="w-full h-full object-cover" />
          )}
        </span>
      ) : (
        <Icon className={`w-3 h-3 flex-shrink-0 ${
          inp.kind === 'submission' ? 'text-emerald-400' :
          inp.kind === 'cba_clip' ? 'text-cyan-400' :
          'text-zinc-400'
        }`} />
      )}
      <span className="flex flex-col min-w-0">
        <span className="text-[10px] text-zinc-300 truncate max-w-[10rem]">{inp.label}</span>
        {inp.filename && (
          <span className="text-[8px] text-zinc-600 truncate max-w-[10rem] font-mono">{inp.filename}</span>
        )}
        {inp.nas_rel_path && (
          <span
            className="text-[8px] text-cyan-500 truncate max-w-[10rem] font-mono mt-0.5"
            title={`NAS: ${inp.nas_rel_path} — click chip to open, or copy this path for Blender / Maya File-Open`}
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              if (navigator.clipboard) {
                navigator.clipboard.writeText(inp.nas_rel_path).catch(() => {});
              }
            }}
          >
            ⎘ NAS: {inp.nas_rel_path}
          </span>
        )}
      </span>
      {inp.url && <ExternalLink className="w-2.5 h-2.5 text-zinc-600 group-hover:text-fuchsia-400 ml-0.5" />}
    </a>
  );
}

/**
 * BriefChip — special raw-material chip for the Lighting Brief.
 * Renders inline in the Raw Material strip with Time-of-Day + BG type
 * tags and the free-text brief on hover/expand. Clicking expands the
 * brief inline (no popover, no navigation — the artist sees it right
 * on their MY TASKS card).
 */
function BriefChip({ brief }) {
  const [expanded, setExpanded] = useState(false);
  const TOD_LABEL = { day: 'DAY', night: 'NIGHT', dawn: 'DAWN', dusk: 'DUSK' };
  const TOD_COLOR = {
    day:   'bg-amber-500/20 text-amber-200 border-amber-500/40',
    night: 'bg-indigo-500/20 text-indigo-200 border-indigo-500/40',
    dawn:  'bg-pink-500/20 text-pink-200 border-pink-500/40',
    dusk:  'bg-orange-500/20 text-orange-200 border-orange-500/40',
  };
  const BG_LABEL = { interior: 'INT', exterior: 'EXT' };
  const todCls = TOD_COLOR[brief.time_of_day] || 'bg-zinc-800 text-zinc-300 border-zinc-700';

  return (
    <button
      type="button"
      onClick={() => setExpanded(e => !e)}
      data-testid="work-card-brief-chip"
      className={`flex flex-col gap-1 px-2 py-1.5 rounded border transition-all text-left ${
        expanded
          ? 'bg-violet-500/10 border-violet-500/50 w-full'
          : 'bg-zinc-900 border-violet-500/30 hover:border-violet-500/60'
      }`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <Sparkles className="w-3 h-3 text-violet-400 flex-shrink-0" />
        <span className="text-[10px] text-violet-200 font-bold uppercase tracking-wider">Lighting Brief</span>
        <span className={`text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${todCls}`}>
          {TOD_LABEL[brief.time_of_day] || brief.time_of_day || '—'}
        </span>
        <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-cyan-500/15 text-cyan-200 border-cyan-500/40 font-bold">
          {BG_LABEL[brief.bg_type] || brief.bg_type || '—'}
        </span>
      </div>
      {expanded ? (
        <p className="text-[10px] text-zinc-300 whitespace-pre-wrap leading-relaxed mt-1">
          {brief.brief || <span className="italic text-zinc-600">No brief text.</span>}
        </p>
      ) : (
        <p className="text-[9px] text-zinc-500 truncate max-w-[16rem]">
          {brief.brief ? `"${brief.brief.slice(0, 60)}${brief.brief.length > 60 ? '…' : ''}"` : 'No brief text.'}
        </p>
      )}
      {expanded && brief.updated_by_name && (
        <p className="text-[8px] text-zinc-600 italic mt-1">
          — {brief.updated_by_name}
          {brief.updated_at ? ` · ${new Date(brief.updated_at).toLocaleDateString()}` : ''}
        </p>
      )}
    </button>
  );
}
